import React, { useState, useEffect } from "react";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import PersonIcon from "@material-ui/icons/Person";
import PhoneIcon from "@material-ui/icons/Phone";
import PrintIcon from "@material-ui/icons/Print";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import LanguageIcon from "@material-ui/icons/Language";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { generalAuthTokenHeader } from "__helpers/auth-header";
import { apiPath } from "api";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import { borderBottom } from "@material-ui/system";
import { userService } from "_services/user.service";
import { EMAIL_REGEX } from "__helpers/constants";

const panelOverlayStyle = {
  position: "fixed",
  top: 0,
  right: 0,
  width: "45%",
  height: "100vh",
  backgroundColor: "#fff",
  boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
  zIndex: 1300,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 20px",
  borderBottom: "1px solid #e0e0e0",
};

const galleryContainerStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gridTemplateRows: "auto auto",
  gap: "8px",
  padding: "16px 20px",
};

const largeImageStyle = {
  gridRow: "1 / 3",
  gridColumn: "1 / 2",
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "6px",
  cursor: "pointer",
  minHeight: "300px",
};

const smallImageStyle = {
  width: "100%",
  height: "150px",
  objectFit: "cover",
  borderRadius: "4px",
  cursor: "pointer",
};

const detailsStyle = {
  padding: "16px 20px",
};

const contactRowStyle = {
  display: "flex",
  alignItems: "center",
  marginBottom: "10px",
  fontSize: "14px",
  color: "#333",
};

const iconStyle = {
  color: "#01A7A6",
  marginRight: "10px",
  fontSize: "20px",
};

const sectionTitleStyle = {
  fontSize: "14px",
  fontWeight: "bold",
  color: "#01A7A6",
  marginBottom: "12px",
  marginTop: "16px",
};

const serviceCategoryCardStyle = {
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "0px 0px 16px",
  marginRight: "10px",
  marginBottom: "10px",
  backgroundColor: "#fff",
  borderRadius: "16px",
  boxShadow: "0px 0px 7.7px 0px #0000004D",
  minWidth: "140px",
  textAlign: "center"
};

const categoryPillStyle = {
  padding: "6px 20px",
  borderRadius: "0px",
  borderTopLeftRadius: "16px",
  borderTopRightRadius: "16px",
  color: "#fff",
  fontSize: "12px",
  width: "100%",
  fontWeight: "400",
  whiteSpace: "nowrap",
  marginBottom: "10px",
  letterSpacing: "0.3px"
};

const categoryColors = ["#01A7A6"];


const requestDetailsContainerStyle = {
  display: "flex",
  alignItems: "center",
  backgroundColor: "#f5f5f5",
  borderRadius: "25px",
  padding: "4px 4px 4px 12px",
  marginTop: "8px",
};

const emailInputStyle = {
  border: "none",
  outline: "none",
  backgroundColor: "transparent",
  flex: 1,
  fontSize: "14px",
  padding: "8px",
};

const getDetailsButtonStyle = {
  backgroundColor: "#E91E63",
  color: "#fff",
  border: "none",
  borderRadius: "20px",
  padding: "8px 20px",
  fontSize: "13px",
  fontWeight: "600",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

function ProviderProfilePanel({ open, providerData, categoryId, totalCategoryIds, onClose, onImageClick }) {
  const [loading, setLoading] = useState(false);
  const [orgData, setOrgData] = useState(null);
  const [sendToEmail, setSendToEmail] = useState("");
  const [sendToEmailValid, setSendToEmailValid] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Extract provider ID from providerData
  const providerId = providerData?.providerId || providerData?.organizationEntity?.[0]?.providerId;

  useEffect(() => {
    if (open && providerId) {
      fetchOrganizationDetails(providerId);
    }
    if (!open) {
      setOrgData(null);
      setSendToEmail("");
      setSendToEmailValid(false);
      setEmailError("");
    }
  }, [open, providerId]);

  const fetchOrganizationDetails = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(
        apiPath.organizationDetailsByProviderId + "?id=" + id,
        {
          method: "GET",
          headers: generalAuthTokenHeader(),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setOrgData(data);
      }
    } catch (error) {
      console.error("Failed to fetch organization details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBrochure = () => {
    if (!orgData || !orgData.organizationBrochureBase64) return;
    const base64 = orgData.organizationBrochureBase64;
    let fileName = "Organization_Brochure.pdf";
    if (base64.startsWith("data:")) {
      const mimeType = base64.split(";")[0].split(":")[1];
      const extMap = {
        "image/jpeg": ".jpg",
        "image/png": ".png",
        "image/gif": ".gif",
        "image/svg+xml": ".svg",
        "application/pdf": ".pdf",
      };
      const ext = extMap[mimeType] || ".pdf";
      fileName = "Organization_Brochure" + ext;
    }
    const link = document.createElement("a");
    link.href = base64;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenWebsite = () => {
    const websiteUrl = providerData?.organizationEntity?.[0]?.websiteUrl;
    if (websiteUrl) {
      let url = websiteUrl;
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = "https://" + url;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const buildFullAddress = () => {
    if (!orgData) return "";
    const parts = [
      orgData.addressStreet,
      orgData.addressSuite ? "Suite " + orgData.addressSuite : null,
      orgData.city,
      orgData.state,
      orgData.zipcode,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const buildLocationAddress = (location) => {
    const parts = [
      location.addressStreet,
      location.addressSuite ? "Suite " + location.addressSuite : null,
      location.city,
      location.state,
      location.zipcode,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setSendToEmail(value);
    if (value.trim().length === 0) {
      setSendToEmailValid(false);
      setEmailError("Email is required");
    } else if (!value.trim().match(EMAIL_REGEX)) {
      setSendToEmailValid(false);
      setEmailError("Invalid email");
    } else {
      setSendToEmailValid(true);
      setEmailError("");
    }
  };

  const handleSendProviderDetails = async (event) => {
    event.preventDefault();
    if (!sendToEmailValid || sendingEmail) return;
    setSendingEmail(true);
    try {
      const id = providerData?.id || providerId;
      const apiUrl = apiPath.sendProviderDetail + "?id=" + id + "&mail=" + sendToEmail;
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: generalAuthTokenHeader(),
      });
      if (response.ok) {
        userService.showNotification({
          title: "Provider Detail",
          message: "Email sent successfully",
          type: "success",
        });
      } else {
        const data = await response.json();
        userService.showNotification({
          title: "Send Mail",
          message: data.title || "Bad response from server",
          type: "danger",
        });
      }
    } catch (error) {
      userService.showNotification({
        title: "Provider Detail",
        message: "Bad response from server",
        type: "danger",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  if (!open) return null;

  const pictures = orgData?.organizationPictures || [];
  const otherLocations = providerData?.otherFilteredAddressEntities || [];
  const allServiceCategories = providerData?.array || [];
  const websiteUrl = providerData?.organizationEntity?.[0]?.websiteUrl;
  const isRegistered = providerData?.isRegistered;
  const orgName = providerData?.organizationEntity?.[0]?.orgName || orgData?.orgName || "N/A";

  // Filter service categories same as listing card
  const serviceCategories = allServiceCategories.filter(category => {
    const catId = category.categoryId;
    const selectedCat = totalCategoryIds?.[categoryId];
    const matchesFilter = selectedCat?.subCategory?.includes(catId) ||
                          selectedCat?.childCategory?.includes(catId);
    const hasValue = category.value && category.value !== "";
    return matchesFilter && hasValue;
  });

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.3)",
          zIndex: 1299,
        }}
        onClick={onClose}
      />

      {/* Panel */}
      <div style={panelOverlayStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <Box display="flex" alignItems="center">
            <span style={{ fontSize: "18px", fontWeight: "bold", color: "#01A7A6" }}>
              Provider Profile
            </span>
            {isRegistered && (
              <CheckCircleIcon
                style={{ color: "#4CAF50", marginLeft: "8px", fontSize: "20px" }}
                titleAccess="Registered Provider"
              />
            )}
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Photo Gallery Grid */}
            {pictures.length > 0 && (
              <div style={galleryContainerStyle}>
                {/* First image (large, spans 2 rows) */}
                <img
                  src={pictures[0].pictureBase64 || pictures[0].pictureUrl}
                  alt={pictures[0].pictureName || "Image 1"}
                  style={largeImageStyle}
                  onClick={() => onImageClick && onImageClick(pictures, 0)}
                />
                {/* Remaining images (small, fill grid) */}
                {pictures.slice(1, 5).map((pic, index) => (
                  <img
                    key={pic.id || index}
                    src={pic.pictureBase64 || pic.pictureUrl}
                    alt={pic.pictureName || `Image ${index + 2}`}
                    style={smallImageStyle}
                    onClick={() => onImageClick && onImageClick(pictures, index + 1)}
                  />
                ))}
              </div>
            )}

            {/* Provider Details */}
            <div style={detailsStyle} className="g-right-sidebar-panel-content-box">
              <Box 
                className="g-content-box"
                style={{
                  backgroundColor: "#F6F6F6",
                  borderRadius: "16px",
                  padding: "16px"
                }}
              >
                {/* Provider Name + Visit Website + Brochure Download */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center">
                    <span style={{ fontSize: "20px", fontWeight: "bold", color: "#000" }}>
                      {orgName}
                    </span>
                    {websiteUrl && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenWebsite}
                        startIcon={<LanguageIcon />}
                        style={{
                          borderColor: "#01A7A6",
                          color: "#01A7A6",
                          textTransform: "none",
                          fontSize: "11px",
                          marginLeft: "10px",
                          padding: "2px 8px",
                          minWidth: "auto",
                        }}
                      >
                        Visit Website
                      </Button>
                    )}
                  </Box>
                  {orgData?.organizationBrochureBase64 && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleDownloadBrochure}
                      startIcon={<PictureAsPdfIcon />}
                      className="g-right-side-popup-button"
                    >
                      Download Brochure
                    </Button>
                  )}
                </Box>

                {/* Contact Person */}
                {orgData && (orgData.primaryFirstName || orgData.primaryLastName) && (
                  <div style={contactRowStyle}>
                    <PersonIcon style={iconStyle} />
                    <span>
                      {[orgData.primaryFirstName, orgData.primaryLastName].filter(Boolean).join(" ")}
                    </span>
                  </div>
                )}

                {/* Phone + Fax (inline) */}
                {(orgData?.phone || orgData?.primaryPhone) && (
                  <div style={contactRowStyle}>
                    <PhoneIcon style={iconStyle} />
                    <span>{orgData.phone || orgData.primaryPhone}</span>
                    {orgData?.fax && (
                      <>
                        <PrintIcon style={{ ...iconStyle, marginLeft: "12px" }} />
                        <span>{orgData.fax}</span>
                      </>
                    )}
                  </div>
                )}
                {/* Fax standalone — only if no phone exists but fax does */}
                {!(orgData?.phone || orgData?.primaryPhone) && orgData?.fax && (
                  <div style={contactRowStyle}>
                    <PrintIcon style={iconStyle} />
                    <span>{orgData.fax}</span>
                  </div>
                )}

                {/* Address */}
                {buildFullAddress() && (
                  <div style={contactRowStyle}>
                    <LocationOnIcon style={iconStyle} />
                    <span>{buildFullAddress()}</span>
                  </div>
                )}
              </Box>

              {/* Service Categories Section */}
              {serviceCategories.length > 0 && (
                <>
                  <div style={sectionTitleStyle}>Service Categories</div>
                  <Box 
                    display="flex" 
                    flexWrap="wrap" 
                    className="g-service-category-box"
                    style={{ marginTop: "16px" }}
                  >
                    {serviceCategories.map((category, index) => {
                      const color = categoryColors[index % categoryColors.length];
                      return (
                        <div
                          key={category.id || index}
                          style={serviceCategoryCardStyle}
                        >
                          <div style={{ ...categoryPillStyle, backgroundColor: color }}>
                            {category.masterCategories?.categoryValue || category.categoryName}
                          </div>
                          {category.value && category.value !== "" && (
                            <div style={{ color: color, fontSize: "24px", fontWeight: "700" }}>
                              ${category.value}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </Box>
                </>
              )}

              {/* Other Locations Section */}
              {otherLocations.length > 0 && (
                <>
                  <div style={sectionTitleStyle}>Other Locations</div>
                  <div style={{
                    backgroundColor: "#F6F6F6",
                    borderRadius: "16px",
                    padding: "12px 16px",
                  }}>
                    {otherLocations.map((location, index) => (
                      <div key={location.id || index} style={{
                        fontSize: "13px",
                        color: "#333",
                        marginBottom: index < otherLocations.length - 1 ? "6px" : 0,
                      }}>
                        <span style={{ color: "#01A7A6", marginRight: "6px" }}>&#9679;</span>
                        <span style={{ fontWeight: "600" }}>
                          {location.title || location.orgName || orgName}
                        </span>
                        {buildLocationAddress(location) && (
                          <span style={{ color: "#666" }}>
                            {" - "}{buildLocationAddress(location)}
                          </span>
                        )}
                        {location.distance !== undefined && location.distance !== null && (
                          <span style={{ color: "#01A7A6", marginLeft: "8px", fontSize: "12px" }}>
                            ({location.distance.toFixed(1)} mi)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Request Provider Details Section */}
              <div style={sectionTitleStyle}>Request Provider Details</div>
              <form onSubmit={handleSendProviderDetails} style={requestDetailsContainerStyle}>
                <MailOutlineIcon style={{ color: "#999", marginRight: "8px" }} />
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={sendToEmail}
                  onChange={handleEmailChange}
                  style={emailInputStyle}
                />
                <button
                  type="submit"
                  disabled={!sendToEmailValid || sendingEmail}
                  style={{
                    ...getDetailsButtonStyle,
                    opacity: (!sendToEmailValid || sendingEmail) ? 0.6 : 1,
                  }}
                >
                  {sendingEmail ? "Sending..." : "Send Details"}
                </button>
              </form>
              {emailError && sendToEmail.length > 0 && (
                <div style={{ color: "red", fontSize: "12px", marginTop: "4px", paddingLeft: "12px" }}>
                  {emailError}
                </div>
              )}
            </div>
          </>
        )}

        {!loading && !orgData && !providerData && (
          <Box p={4} textAlign="center" color="#999">
            No organization details found.
          </Box>
        )}
      </div>
    </>
  );
}

export default ProviderProfilePanel;
